import type {
  GlobalQualification,
  GlobalQualificationPayload,
  LocalQualification,
  LocalQualificationPayload,
  Qualification,
} from '@/libs/interfaces'

import { mockGlobalQualifications, mockLocalQualifications } from '../fixtures/qualifications'
import { delay, genID } from '../helpers'

let globalQualifications: GlobalQualification[] = [...mockGlobalQualifications]
let localQualifications: LocalQualification[] = [...mockLocalQualifications]

export const qualificationsApiMock = {
  upsertGlobalQualification: async (
    params: { orgID: string } & GlobalQualificationPayload,
  ): Promise<Partial<GlobalQualification>> => {
    const upserted = params.quals.map((qual) => ({
      ...qual,
      globalQualID: qual.globalQualID ?? genID('global-qual'),
      orgID: params.orgID,
    }))
    const upsertedIds = new Set(upserted.map((qual) => qual.globalQualID))
    globalQualifications = [
      ...globalQualifications.filter(
        (qual) => !qual.globalQualID || !upsertedIds.has(qual.globalQualID),
      ),
      ...upserted,
    ]
    return delay(upserted[0] ?? {})
  },

  upsertLocalQualification: async (
    params: { storeID: string } & LocalQualificationPayload,
  ): Promise<Partial<LocalQualification>> => {
    const upserted = params.quals.map((qual) => ({
      ...qual,
      localQualID: qual.localQualID ?? genID('local-qual'),
      storeID: params.storeID,
    }))
    const upsertedIds = new Set(upserted.map((qual) => qual.localQualID))
    localQualifications = [
      ...localQualifications.filter(
        (qual) => !qual.localQualID || !upsertedIds.has(qual.localQualID),
      ),
      ...upserted,
    ]
    return delay(upserted[0] ?? {})
  },

  deleteGlobalQualification: async (
    params: Partial<GlobalQualification>,
  ): Promise<GlobalQualification> => {
    const removed = globalQualifications.find((qual) => qual.globalQualID === params.globalQualID)
    globalQualifications = globalQualifications.filter(
      (qual) => qual.globalQualID !== params.globalQualID,
    )
    return delay(removed ?? {})
  },

  deleteLocalQualification: async (
    params: Partial<LocalQualification>,
  ): Promise<LocalQualification> => {
    const removed = localQualifications.find((qual) => qual.localQualID === params.localQualID)
    localQualifications = localQualifications.filter(
      (qual) => qual.localQualID !== params.localQualID,
    )
    return delay(removed ?? {})
  },

  list: async (orgID: string, _query: string): Promise<Qualification> => {
    const orgGlobalQuals = globalQualifications.filter((qual) => qual.orgID === orgID)
    return delay({
      message: 'OK',
      totalGlobalQuals: orgGlobalQuals.length,
      totalLocalQuals: localQualifications.length,
      globalQuals: orgGlobalQuals,
      localQuals: localQualifications,
    })
  },

  getByIDGlobal: async (orgID: string, globalQualID: string): Promise<GlobalQualification> =>
    delay(
      globalQualifications.find(
        (qual) => qual.globalQualID === globalQualID && qual.orgID === orgID,
      ) ?? {},
    ),

  getByIDLocal: async (storeID: string, localQualID: string): Promise<LocalQualification> =>
    delay(
      localQualifications.find(
        (qual) => qual.localQualID === localQualID && qual.storeID === storeID,
      ) ?? {},
    ),
}
